import React, { useMemo } from 'react';
import { Box, Field, FieldTypes, Inscription } from '@common-fe/common-fe';

import { REGEXPS, VALIDATORS } from '@/common';
import { FieldStateKey, PermissionKey } from '@/common/constants';
import { useHideFields } from '@/modules/employer/components/SetupEmployer/hooks';
import { MultiFactorAuthenticationValues } from '@/modules/employer/components/SetupEmployer/SetupEmployer.types';
import { EmployerDto } from '@/modules/employer/types';
import useGetPermissionToEditOverridableField from '@/modules/user/hooks/useGetPermissionToEditOverridableField';

interface Props {
  organizationData?: EmployerDto;
  multiFactorAuthenticationValues?: MultiFactorAuthenticationValues | null;
}

const useMultiFactorAuthenticationFields = ({ organizationData, multiFactorAuthenticationValues }: Props) => {
  const requireParticipantMfa = useMemo(() => organizationData?.overridableProperties?.requireParticipantMfa, [organizationData]);
  const requireAdminMfa = useMemo(() => organizationData?.overridableProperties?.requireAdminMfa, [organizationData]);
  const accountLockoutTime = useMemo(() => organizationData?.overridableProperties?.accountLockoutTime, [organizationData]);

  const authTypePermProps = useGetPermissionToEditOverridableField(
    PermissionKey.AUTHENTICATION_TYPE,
    FieldStateKey.AUTHENTICATION_TYPE
  );
  const accountLockoutTimePermProps = useGetPermissionToEditOverridableField(
    PermissionKey.ACCOUNT_LOCKOUT_TIME,
    FieldStateKey.ACCOUNT_LOCKOUT_TIME
  );

  const fields = useMemo(() => [
    {
      name: 'requireParticipantMfa',
      label: (
        <Box>
          <Inscription style={{ maxWidth: '233px' }}>
            Require Participant Multi Factor Authentication
          </Inscription>
        </Box>
      ),
      type: FieldTypes.Checkbox,
      defaultValue: requireParticipantMfa,
      ...authTypePermProps,
    },
    {
      name: 'requireAdminMfa',
      label: (
        <Box>
          <Inscription style={{ maxWidth: '233px' }}>
            Require Admin Multi Factor Authentication
          </Inscription>
        </Box>
      ),
      type: FieldTypes.Checkbox,
      defaultValue: requireAdminMfa,
      ...authTypePermProps,
    },
    ...(multiFactorAuthenticationValues?.requireParticipantMfa || multiFactorAuthenticationValues?.requireAdminMfa ? [
      {
        name: 'accountLockoutTime',
        label: 'Account Lockout Time (min)',
        placeholder: 'Account Lockout Time',
        type: FieldTypes.Number,
        defaultValue: accountLockoutTime || 30,
        value: accountLockoutTime || 30,
        regexp: REGEXPS.ACCOUNT_LOCKOUT_TIME_NUMBERS,
        validator: VALIDATORS.REQUIRED_STRING,
        ...accountLockoutTimePermProps,
      }
    ] : []),
  ], [
    requireParticipantMfa,
    requireAdminMfa,
    accountLockoutTime,
    multiFactorAuthenticationValues,
    authTypePermProps,
    accountLockoutTimePermProps,
  ]);
  const formattedFields = useHideFields(fields as Field[]);
  return formattedFields;
};

export default useMultiFactorAuthenticationFields;
